/**
 * Tiny notification sounds — synthesized via Web Audio so we don't ship
 * any binary audio assets.
 *
 * Two variants:
 *   - `playIncoming()` — soft two-tone "ding" (E5 → A5) for a received
 *     chat message.
 *   - `playSelfSend()` — subtle single click for the user's own send
 *     (currently unused; kept for future use).
 *
 * Sound is muted by default until the user has interacted with the page
 * — every modern browser blocks AudioContext creation before a user
 * gesture. We lazily create the context on the first attempted play and
 * silently no-op on the very first pre-interaction attempt.
 *
 * A per-device mute preference is stored in localStorage under
 * `ch_chat_sound` ("on" | "off", default "on"). Toggling from the chat
 * header updates that key.
 */
const MUTE_KEY = "ch_chat_sound";
let ctx = null;
let unlocked = false;
let unlockAttached = false;

function ensureCtx() {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  try {
    ctx = new AudioCtx();
    return ctx;
  } catch {
    return null;
  }
}

/**
 * Attach a one-shot listener that resumes the AudioContext on the FIRST
 * user interaction anywhere in the app (tap, click, keydown, or a
 * bootstrap "silent audio" hint). This is required because:
 *   - Every mobile browser starts new AudioContexts in "suspended".
 *   - `startRing()` is called from a poll handler, NOT from a click,
 *     so it can't unlock the context itself.
 *   - Once unlocked (via user gesture), the same context stays alive
 *     for the whole session and later programmatic tones just work.
 *
 * Safe to call multiple times — installs the listener exactly once and
 * bails out fast after the first successful unlock.
 */
export function primeAudioUnlock() {
  if (typeof window === "undefined") return;
  if (unlocked || unlockAttached) return;
  unlockAttached = true;
  const attemptUnlock = () => {
    if (unlocked) return;
    const audio = ensureCtx();
    if (!audio) return;
    // resume() may reject silently on iOS if this isn't actually
    // running inside a user gesture — that's fine, we'll try again
    // on the next event until it sticks.
    const p = audio.resume?.();
    // Also schedule an inaudible tone so iOS actually starts the
    // audio hardware pipeline — otherwise the FIRST real ring is
    // sometimes clipped on iPhone.
    try {
      const g = audio.createGain();
      g.gain.value = 0;
      const osc = audio.createOscillator();
      osc.frequency.value = 1;
      osc.connect(g).connect(audio.destination);
      osc.start();
      osc.stop(audio.currentTime + 0.01);
    } catch { /* ignore */ }
    Promise.resolve(p).then(() => {
      if (audio.state === "running") {
        unlocked = true;
        // Once we're unlocked we can drop the listeners; leaving
        // them attached would fire on every subsequent tap for
        // nothing.
        ["pointerdown", "click", "keydown", "touchend"].forEach((ev) => {
          window.removeEventListener(ev, attemptUnlock, true);
        });
      }
    }).catch(() => { /* try again on next tap */ });
  };
  ["pointerdown", "click", "keydown", "touchend"].forEach((ev) => {
    window.addEventListener(ev, attemptUnlock, true);
  });
}

export function isSoundEnabled() {
  if (typeof window === "undefined") return false;
  return (localStorage.getItem(MUTE_KEY) || "on") !== "off";
}

export function setSoundEnabled(on) {
  if (typeof window === "undefined") return;
  localStorage.setItem(MUTE_KEY, on ? "on" : "off");
}

/** Play a short tone. Returns immediately whether or not it succeeded. */
function tone({ freq = 880, duration = 0.18, gain = 0.05, delay = 0 } = {}) {
  const audio = ensureCtx();
  if (!audio) return;
  const now = audio.currentTime + delay;
  const osc = audio.createOscillator();
  const g = audio.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(freq, now);
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(gain, now + 0.015);
  g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  osc.connect(g).connect(audio.destination);
  osc.start(now);
  osc.stop(now + duration + 0.02);
}

export function playIncoming() {
  if (!isSoundEnabled()) return;
  // Soft two-note ding (E5 → A5) — friendly, non-intrusive.
  tone({ freq: 659.25, duration: 0.14, gain: 0.06 });
  tone({ freq: 880, duration: 0.20, gain: 0.05, delay: 0.11 });
}

export function playSelfSend() {
  if (!isSoundEnabled()) return;
  tone({ freq: 1100, duration: 0.08, gain: 0.03 });
}

/**
 * Incoming-call ringtone. Repeating warble (A5 → E5) that continues
 * every ~1.6s until `stopRing()` is called. Respects the same mute
 * preference as chat sounds so a member who muted chat also gets a
 * silent visual ring (the modal still appears).
 */
let ringInterval = null;
export function startRing() {
  if (ringInterval) return; // already ringing
  if (typeof window === "undefined") return;
  // If a poll finds an incoming invite BEFORE the user has tapped
  // anything after login, `ctx.state` will still be "suspended".
  // Try one last resume here — this succeeds when the app is already
  // in the foreground and the browser trusts the tab.
  const audio = ensureCtx();
  try { audio?.resume?.().catch(() => {}); } catch { /* ignore */ }
  const play = () => {
    if (!isSoundEnabled()) return;
    // Fatter tones (0.16 gain vs 0.10 before) so the ringtone is
    // audible without the phone pressed to the ear — matches how a
    // real phone sounds through a room-speaker.
    tone({ freq: 880, duration: 0.28, gain: 0.16 });
    tone({ freq: 659.25, duration: 0.28, gain: 0.16, delay: 0.35 });
    // Vibration pattern matches the two-tone audio rhythm — two
    // ~350ms buzzes with a short gap between, then a longer pause
    // that lines up with the next audio ring 1.6s later. iOS Safari
    // silently ignores `navigator.vibrate` (Apple doesn't expose the
    // Vibration API to Safari), which is fine — audio still plays.
    // Respects the same mute pref so a user who silenced chat sounds
    // also gets a silent phone.
    try {
      navigator.vibrate?.([350, 150, 350, 750]);
    } catch { /* some Android browsers throw when permission was denied */ }
  };
  play();
  ringInterval = window.setInterval(play, 1600);
}

export function stopRing() {
  if (ringInterval) {
    clearInterval(ringInterval);
    ringInterval = null;
  }
  // Passing 0 (or an empty array) cancels an in-flight vibration
  // sequence so the phone doesn't keep buzzing after the callee
  // answers or the caller hangs up. No-op on iOS.
  try { navigator.vibrate?.(0); } catch { /* ignore */ }
}
